/**
 * Skills List Command
 * List locally installed skills or available skills on server
 */
import { Command } from 'commander';
import chalk from 'chalk';
import Table from 'cli-table3';
import { ConfigService } from '../../services/config.service.js';
import { SkillsService } from '../../services/skills.service.js';
import { ValidationError } from '../../errors/index.js';

export function createSkillsListCommand(): Command {
    return new Command('list')
        .description('List installed skills or available skills on server')
        .option('--available', 'List all skills available on Jai1 server')
        .option('-s, --search <term>', 'Search skills by name or description')
        .option('--json', 'Output machine-readable JSON')
        .action(async (options: { available?: boolean; search?: string; json?: boolean }, command: Command) => {
            const skillsService = new SkillsService();
            const rootOptions = command.parent?.parent?.opts<{ json?: boolean }>();
            const jsonMode = options.json === true || rootOptions?.json === true;

            if (options.available) {
                // List from server
                const configService = new ConfigService();
                const config = await configService.load();

                if (!config) {
                    throw new ValidationError('Chưa xác thực. Chạy "jai1 auth" trước.');
                }

                console.log(chalk.cyan('📦 Đang tải danh sách skills từ server...'));
                console.log();

                const results = await skillsService.searchFromServer(config, options.search);

                if (jsonMode) {
                    console.log(JSON.stringify({
                        schemaVersion: 1,
                        scope: 'available',
                        skills: results.map((skill) => ({
                            slug: skill.filepath.replace(/^skills\//, ''),
                            name: skill.name,
                            description: skill.description || '',
                            source: 'jai1',
                            version: skill.version,
                            popularity: skill.downloads || 0,
                            tags: skill.tags || [],
                        })),
                    }, null, 2));
                    return;
                }

                if (results.length === 0) {
                    console.log(chalk.yellow('Không tìm thấy skills nào.'));
                    return;
                }

                const table = new Table({
                    head: [
                        chalk.cyan('Tên'),
                        chalk.cyan('Mô tả'),
                        chalk.cyan('Version'),
                        chalk.cyan('Downloads'),
                    ],
                    style: { head: [], border: ['gray'] },
                    colWidths: [28, 40, 10, 12],
                });

                for (const skill of results) {
                    const name = skill.filepath.replace('skills/', '');
                    table.push([
                        chalk.white(name),
                        chalk.dim((skill.description || '').slice(0, 38)),
                        chalk.green(skill.version || '-'),
                        chalk.dim(String(skill.downloads || 0)),
                    ]);
                }

                console.log(table.toString());
                console.log();
                console.log(chalk.dim(`Tổng cộng: ${results.length} skill(s)`));
                console.log(chalk.dim('\n💡 Dùng "j skills add <tên>" để cài đặt'));
            } else {
                // List local
                const projectRoot = process.cwd();
                const skills = await skillsService.listLocal(projectRoot);

                if (jsonMode) {
                    console.log(JSON.stringify({
                        schemaVersion: 1,
                        scope: 'local',
                        skills,
                    }, null, 2));
                    return;
                }

                if (skills.length === 0) {
                    console.log(chalk.yellow('Chưa có skills nào được cài đặt.'));
                    console.log();
                    console.log(chalk.dim('💡 Dùng "j skills add <tên>" để cài đặt'));
                    console.log(chalk.dim('   hoặc "j skills list --available" để xem skills có sẵn'));
                    return;
                }

                console.log(chalk.bold.cyan('🛠 Skills đã cài đặt'));
                console.log();

                const table = new Table({
                    head: [
                        chalk.cyan('Tên'),
                        chalk.cyan('Mô tả'),
                        chalk.cyan('Files'),
                    ],
                    style: { head: [], border: ['gray'] },
                    colWidths: [28, 45, 8],
                });

                for (const skill of skills) {
                    table.push([
                        chalk.white(skill.slug),
                        chalk.dim(skill.description.slice(0, 43)),
                        chalk.dim(String(skill.fileCount)),
                    ]);
                }

                console.log(table.toString());
                console.log();
                console.log(chalk.dim(`Tổng cộng: ${skills.length} skill(s)`));
                console.log(chalk.dim('\n💡 Dùng "j skills sync" để đồng bộ sang IDE(s)'));
            }
        });
}
